import React, { useCallback, useContext, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import LogRocket from 'logrocket';
import _get from 'lodash/get';

import { AuthContext } from './AuthContext';
import useStore from 'utils/hooks/useStore';
import useUserMetaData from 'utils/hooks/useUserMetaData';
import isInternalUser from 'utils/methods/isInternalUser';

const RR_API_KEY = process.env.REACT_APP_RR_LOG_ROCKET_APP_ID;
const SSR_API_KEY = process.env.REACT_APP_SSR_LOG_ROCKET_APP_ID;
const IS_PROD = process.env.REACT_APP_MODE === 'PRODUCTION';

/**
 * @type {import('react').Context<{
 *   track: (eventName: string, eventProperties?: Record<string, any>) => void;
 * }>}
 */
export const LogRocketContext = React.createContext();

export function LogRocketProvider({ children = null }) {
  const { userData } = useContext(AuthContext);
  const { getStoreProperty } = useStore();
  const userMetaData = useUserMetaData();

  const userId = _get(userData, 'userId');
  const email = _get(userData, 'email');
  const isSSR = getStoreProperty('is_ssr_retailer', false);

  const apiKey = isSSR ? SSR_API_KEY : RR_API_KEY;
  const isTrackable = apiKey && IS_PROD && !isInternalUser(email);

  useEffect(() => {
    if (!isTrackable) return;

    try {
      LogRocket.init(apiKey, {
        console: { shouldAggregateConsoleErrors: true },
        release: `${process.env.REACT_APP_VERSION}-${process.env.REACT_APP_CURRENT_GIT_SHA}`,
      });
      LogRocket.getSessionURL((sessionURL) => {
        Sentry.getCurrentScope().setExtra('sessionURL', sessionURL);
      });
    } catch (error) {
      console.error('Failed to initialize LogRocket:', error);
    }
  }, [isTrackable, apiKey]);

  useEffect(() => {
    if (!isTrackable || !userId) return;

    try {
      LogRocket.identify(userId, userMetaData);
    } catch (error) {
      console.error('Failed to identify user in LogRocket:', error);
    }
  }, [isTrackable, userId, userMetaData]);

  const track = useCallback(
    (eventName = '', eventProperties = {}) => {
      if (!isTrackable || !eventName) return;

      try {
        LogRocket.track(eventName, {
          ...userMetaData,
          ...eventProperties,
        });
      } catch (error) {
        console.error('Failed to track event in LogRocket:', error);
      }
    },
    [isTrackable, userMetaData],
  );

  return (
    <LogRocketContext.Provider value={{ track }}>
      {children}
    </LogRocketContext.Provider>
  );
}
